import React, { useState, useEffect } from 'react';
import { useTable, usePagination, useSortBy } from 'react-table';
import ReactPaginate from 'react-paginate';
import { FaSearch } from 'react-icons/fa'; // Import icons
import { allQueries, sendQueryResponse, sendMoneyToShoveller } from '../../../apiManager/admin/QueriesManagement.js';
import { enableCancelButtonForHomeOwner } from '../../../apiManager/admin/JobsManagement.js';
import { Loader } from '../../../components/base/Loader';
import Modal from './queryComponent.jsx'
import ReplyModal from './replyComponent.jsx';
import api from '../../../apiManager/api.js';
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Search } from "lucide-react";


const ReasonModal = ({ isOpen, onClose, onSendPayment, content }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white p-6 rounded shadow-lg w-1/3">
                <h2 className="text-xl font-bold mb-4">Job Information</h2>
                
                <div className="mb-3">
                    <strong>Reason:</strong> <span className="ml-2">{content.reason}</span>
                </div>
                <div className="mb-3">
                    <strong>Job Status:</strong> <span className="ml-2">{content.jobStatus}</span>
                </div>
                <div className="mb-3">
                    <strong>Payout Status:</strong> <span className="ml-2">{content.payoutStatus ? content.payoutStatus : 'Pending'}</span>
                </div>
                <div className="mb-3">
                    <strong>Payment Captured:</strong> <span className="ml-2">{content.paymentCaptured === 'capture' ? 'Yes' : 'No'}</span>
                </div>

                {content.jobStatus === 'completed' && content.payoutStatus === 'failed' && content.paymentCaptured === 'capture' && (
                    <div className="flex justify-end mt-4">
                        <button onClick={() => onSendPayment(content)} className="bg-blue-500 hover:bg-blue-700 text-white px-4 py-2 rounded">
                            Retry Payment
                        </button>
                    </div>
                )}

                <div className="flex justify-end mt-6 space-x-4">
                    <button onClick={onClose} className="bg-gray-500 hover:bg-gray-700 text-white px-4 py-2 rounded">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};




function CustomerTable() {
    const [queries, setQueries] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
    const [selectedQueryId, setSelectedQueryId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('status')
    const [jobId, setJobId] = useState(null);

    const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
    const [reasonContent, setReasonContent] = useState({})
    const [currentPage, setCurrentPage] = useState(0);
    const [totalCount, setTotalCount] = useState(10);
    const rowsPerPage = 8;

    useEffect(() => {
        (async function () {
            const res = await api.get(`/query/getAllQueries`);
            setTotalCount(res.data.count);
        })();
    }, [])

    useEffect(() => {
        const getQueriesData = async () => {
            try {
                setLoading(true);
                const res = await allQueries(currentPage + 1);
                if(res.count === 0 || res.error){
                    setError('No queries available');
                    return;
                }
                console.log(res)
                if (!res) setQueries([]);
                else setQueries(res.allQueries);
            } catch (error) {
                setError(error.message || "An error occurred while fetching data");
            } finally {
                setLoading(false);
            }
        }
        getQueriesData();
    }, [jobId,selectedQueryId, currentPage]);

    const handleTitleClick = (content) => {
        setModalContent(content);
        setIsModalOpen(true);
    };


    const handleReasonClick = (content) => {
        setIsReasonModalOpen(true);
        setReasonContent(content);
    }

    const handleReplyClick = (queryId) => {
        setIsModalOpen(false);
        setSelectedQueryId(queryId);
        setIsReplyModalOpen(true);
    };


    //send money to shoveller
    const handleSendPayment = async (content) => {
        try {
            setLoading(true);
            const res = await sendMoneyToShoveller(content.id, content.jobId, content.queryId, content.payment);
            if (res && res.error) {
                setError(res.error);
                setLoading(false);
                return;
            }
            alert('Payment sent successfully');
        } catch (error) {
            setError(error.message || "An error occurred while sending payment");
        }
        finally {
            setLoading(false);
        }

        setIsReasonModalOpen(false)
    }

    const handleSubmitReply = async (replyText) => {
        setLoading(true);
        try {
            if (!replyText.trim()) {
                setError('Reply text cannot be empty');
                setLoading(false);
                return;
            }
    
            const res = await sendQueryResponse(selectedQueryId, replyText);
            if (res && res.error) { 
                setError(res.error);
                setLoading(false);
                return;
            }
            
            //udpate the query state as well 
            const updatedQueries = queries.map(query => {
                if (query.query._id === selectedQueryId) {
                    query.query.status = 'closed';
                }
                return query;
            });
            setQueries(updatedQueries);
            
            // Close the reply modal after submitting
            setIsReplyModalOpen(false);
            alert('Reply submitted successfully');
        } catch (error) {
            setError(error.error || "An error occurred while sending reply");
        }
        finally {
            setLoading(false);
        }
    };

    const handlerForEnableCancelButton = async (row) => {
        setLoading(true);
        try{
            const res = await enableCancelButtonForHomeOwner(row.original.query.jobId);
            if(res && res.error){
                setError(res.error);
                setLoading(false);
                return;
            }
            alert('Cancel button enabled!');

        }catch(error)
        {
            setError(error.error || "An error occurred while enabling cancel button");
        }finally{
            setLoading(false);

            //open the query reply modal
            handleReplyClick(row.original.query._id);
        }
    }

    const columns = React.useMemo(
        () => [
            {
                Header: 'User Name',
                accessor: 'name',
                Cell: ({ row }) => (
                    <span className="text-left">{row.original.name}</span>
                ),

            },
            {
                Header: 'Title',
                accessor: 'title',
                Cell: ({ row }) => (
                    <span className="text-left cursor-pointer"
                    onClick={() => handleTitleClick(row.original.query.query)}
                    >
                        {row.original.query.title}
                    </span>
                ),
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ row }) => {
                    const currentStatus = row.original.query.status;

                    const handleStatusChange = (newStatus) => {
                        const updatedCustomers = [...queries];
                        updatedCustomers[row.index].status = newStatus;
                        setQueries(updatedCustomers);
                    };

                    return (
                        <div className="flex space-x-2">
                            <button
                                className={`px-2 py-1 rounded bg-zinc-400 text-white`}
                            >
                                {row.original.query.status}
                            </button>
                        </div>
                    );
                },
            },
            {
                Header: 'Role',
                accessor: 'role',
                Cell: ({ row }) => (
                    <span className="text-left">{row.original.role}</span>
                ),
            },
            {
                Header: 'Reason',
                accessor: 'reason',
                Cell: ({ row }) => (
                    row.original.role === 'shoveller' &&
                    <span className="text-left cursor-pointer"
                    onClick={() => handleReasonClick({
                        reason: row.original.query.reason ? row.original.query.reason : 'Account is ok',
                        id: row.original.query.userId,
                        jobId: row.original.job._id,
                        queryId:row.original.query._id,
                        payment:row.original.job.paymentInfo.amount,
                        // Match the shovellerId and then get the payout status from the object
                       
                        payoutStatus: row.original.job.ShovelerInfo.find(info => info.ShovelerId === row.original.query.userId) ? row.original.job.ShovelerInfo.find(info => info.ShovelerId === row.original.query.userId).PayoutStatus : 'pending',
                        jobStatus:row.original.job.jobStatus,
                        paymentCaptured:row.original.job.paymentInfo.status
                    })}
                    >Show Reason</span>
                ),
            },
            {
                Header: 'Enable Cancel',
                accessor: 'enable cancel',
                Cell: ({ row }) => (
                    (row.original.role === 'houseOwner' && row.original.query.status === 'open') &&
                    <button
                        onClick={() => handlerForEnableCancelButton(row)}
                        className="bg-black text-white px-1 py-1 rounded-md">Enable</button>
                ),
            },
            {
                Header: 'Send Email',
                accessor: 'send email',
                Cell: ({ row }) => (
                    <button
                        onClick={() => handleReplyClick(row.original.query._id)}
                        className="bg-black text-white px-1 py-1 rounded-md">Send Email</button>
                ),
            }
        ],
        [queries] // Include customers in dependencies to re-render on state change
    );

    const data = React.useMemo(() => {
        return queries
            .filter((query) =>
                query.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .filter((query) => {
            // Check if "All" is selected, if yes, don't apply the status filter
            if (sortBy.toLowerCase() === 'status') {
                return true; // Include all shovelers
            }
            return query.query.status.toLowerCase() === sortBy.toLowerCase();
        });
    }, [queries, searchTerm, sortBy]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        rows,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: data,
            initialState: { pageIndex: 0, pageSize: 8 },
        },
        useSortBy,
        usePagination
    );

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
    };

    

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <p className="text-red-500">{error}</p>;
    }

    if (!queries.length) {
        return <p>No queries available</p>;
    }

    return (
        <section className="">
            <div className="mt-6 mb-12">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
  <div className="flex flex-col gap-0">
    <h2 className="text-2xl font-semibold tracking-tight">All Queries</h2>
  </div>
  
  <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
    <div className="relative w-full sm:w-auto">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        placeholder="Search..."
        className="pl-10 w-full sm:w-[200px] lg:w-[250px]"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>
    
    <Select value={sortBy} onValueChange={setSortBy}>
      <SelectTrigger className="w-full sm:w-[180px]">
        <SelectValue placeholder="Sort by status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="status">Status</SelectItem>
        <SelectItem value="closed">Closed</SelectItem>
        <SelectItem value="open">Open</SelectItem>
      </SelectContent>
    </Select>
  </div>
</div>

                {/* Table */}
                <div className='overflow-x-auto'>
                    <table {...getTableProps()} className="w-full mt-10 text-base font-medium tracking-normal text-gray-400 border-collapse">
                        <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                            className="px-4 py-2 text-left border-b-0 text-sm" // Increased padding and font size
                                        >
                                            {column.render('Header')}
                                            {column.canSort && (
                                                <span className={`ml-2 ${column.isSorted ? '' : 'invisible'}`}>
                                                    {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                                </span>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()} className="border-b-0">
                            {rows.map(row => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} className="border-b-0">
                                        {row.cells.map(cell => {
                                            return (
                                                <td
                                                    {...cell.getCellProps()}
                                                    className="px-4 py-2 border-b-0 text-sm" // Increased padding and font size
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal component */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                content={modalContent}
            />

            {/* Modal for replying to the query */}
            <ReplyModal
                isOpen={isReplyModalOpen}
                onClose={() => setIsReplyModalOpen(false)}
                onSubmitReply={handleSubmitReply}
            />

            {isReasonModalOpen && (
                <ReasonModal
                isOpen={isReasonModalOpen}
                onClose={ () => setIsReasonModalOpen(false)}
                content={reasonContent}
                onSendPayment={handleSendPayment}
                />
                
            )}

            {/* Pagination */}
            <ReactPaginate className='pagination-container flex justify-end me-32 mt-10 gap-x-5'
                previousLabel={
                    <span className="flex items-center justify-center px-2 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out">
                        &lt;
                    </span>
                }
                nextLabel={
                    <span className="flex items-center justify-center px-2 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out">
                        &gt;
                    </span>
                }
                breakLabel={<span className="mx-2 text-gray-600">...</span>}
                breakClassName={`break-me`}
                pageCount={Math.ceil(totalCount/rowsPerPage)}
                forcePage={currentPage}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageClick}
                containerClassName={'flex items-center'}
                pageLinkClassName={'bg-gray-200 px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out'}
                activeLinkClassName={'bg-zinc-900 text-white rounded-md'}
                disabledClassName={'opacity-50 cursor-not-allowed'}
            />

        </section >
    );
}

export default CustomerTable;
